import React, { useCallback, useRef, useState } from "react";
import * as S from "./BrandPrinciplesHero.styles";
import { IHeroProps, IHeroSection } from "./BrandPrinciplesHero.types";
import { useTranslation } from "hooks/useTranslation";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { BrandPrinciplesHeroVideo } from "./BrandPrinciplesHeroVideo";
import { BrandPrinciplesHeroTabs } from "./BrandPrinciplesHeroTabs";

export const BrandPrinciplesHero = ({
  isInView,
  slides,
}: Readonly<IHeroProps>) => {
  const ref = useRef({});
  const container = useRef<HTMLDivElement>(null);
  const tabRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState(slides[0]);
  const [videoPlaybackTime, setVideoPlaybackTime] = useState<{
    [key: number]: number;
  }>({});

  useGSAP(
    () => {
      const timeline = gsap.timeline();

      timeline
        .fromTo(
          container.current,
          { opacity: 0, y: "15%", delay: 0.1 },
          { opacity: 1, y: 0, duration: 0.75, ease: "power2.out" },
        )
        .fromTo(
          tabRef.current,
          { opacity: 0, y: "15%" },
          {
            opacity: 1,
            y: 0,
            delay: -0.25,
            duration: 0.55,
            ease: "power2.out",
          },
        );
    },
    { dependencies: [], scope: container },
  );

  const handleVideoDurationUpdate = (
    event: React.SyntheticEvent<HTMLVideoElement>,
    id: number,
  ) => {
    const { duration } = event.currentTarget;

    if (duration) {
      setVideoPlaybackTime((prev) => ({
        ...prev,
        [id]: duration,
      }));
    }
  };

  const handleClick = (tab: IHeroSection) => {
    if (tab.id === activeTab.id) {
      return;
    }

    setActiveTab(tab);
  };

  const switchNextTab = async () => {
    const isLastIndex = activeTab.id + 1 >= slides.length;
    const correctedIndex = isLastIndex ? 0 : activeTab.id + 1;

    try {
      ref.current[correctedIndex].currentTime = 0;
      await ref.current[correctedIndex].play();
    } catch (error) {
      console.error("Error playing video:", error);
    }

    setActiveTab(slides[correctedIndex]);
  };

  const switchPreviousTab = async () => {
    const isFirstIndex = activeTab.id === 0;
    const correctedIndex = isFirstIndex ? slides.length - 1 : activeTab.id - 1;

    try {
      ref.current[correctedIndex].currentTime = 0;
      await ref.current[correctedIndex].play();
    } catch (error) {
      console.error("Error playing video:", error);
    }

    setActiveTab(slides[correctedIndex]);
  };

  const switchToNextSlide = useCallback(() => {
    setActiveTab((prev) => {
      const nextIndex = prev.id + 1;
      const isLastIndex = nextIndex >= slides.length;
      const correctedIndex = isLastIndex ? 0 : nextIndex;

      return slides[correctedIndex];
    });
  }, [slides]);

  return (
    <S.HeroContainer>
      <S.Wrapper>
        <S.Content ref={container}>
          <S.MainTitle
            renderAs="h1"
            title={t(activeTab?.label)}
            isCentered
            isMainTitle
            titleWeight={900}
            spacing={{ top: 32, bottom: 24 }}
          />

          <S.TabContainer ref={tabRef}>
            <BrandPrinciplesHeroTabs
              activeTab={activeTab}
              slides={slides}
              videoPlaybackTime={videoPlaybackTime}
              isInView={isInView}
              onHandleTabClick={handleClick}
            />
          </S.TabContainer>
        </S.Content>

        <S.BackgroundVideoWrapper>
          <S.InvisibleButton
            aria-label="Switch to previous video"
            onClick={switchPreviousTab}
          />

          {slides?.map((section, index) => (
            <BrandPrinciplesHeroVideo
              key={`${section.label}-${index}`}
              section={section}
              activeTab={activeTab}
              isInView={isInView}
              onVideoEnded={switchToNextSlide}
              onLoadedMetadata={(e) => {
                handleVideoDurationUpdate(e, section.id);
              }}
            />
          ))}

          <S.InvisibleButton
            aria-label="Switch to next video"
            $isRight
            onClick={switchNextTab}
          />
        </S.BackgroundVideoWrapper>

        <S.Gradient />
      </S.Wrapper>
    </S.HeroContainer>
  );
};
